import React, { useState, useEffect } from 'react';
import { supabase } from '../../../supabase';
import { useNavigate, Link } from 'react-router-dom';
import styles from './Hackathon.module.css';
import Textarea from '../../../components/common/Textarea';
import Button from '../../../components/common/Button';
import TitleSection from './components/TitleSection';
import TabMenu from './components/TabMenu';
import Input from '../../../components/common/Input';
import { ICON_PATHS } from '../../../constants/iconPaths';
import { Checkbox } from '../../../components/common/Checkbox';
import { Helmet } from 'react-helmet-async';

// 아이디어 등록 모달을 컴포넌트 바깥으로 분리
const IdeaModal = ({ isOpen, onClose, onSubmit, ideaForm, setIdeaForm }) => {
  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose} />
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>아이디어 등록</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d={ICON_PATHS.close} fill="currentColor" />
            </svg>
          </button>
        </div>
        <div className={styles.modalContent}>
          <div className={styles.inputGroup}>
            <label>제목</label>
            <Input
              value={ideaForm.title}
              onChange={(e) => setIdeaForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="아이디어의 제목을 입력해주세요"
            />
          </div>
          <div className={styles.inputGroup}>
            <label>해결하고자 하는 문제</label>
            <Textarea
              value={ideaForm.problem}
              onChange={(e) => setIdeaForm(prev => ({ ...prev, problem: e.target.value }))}
              placeholder="어떤 문제를 해결하고 싶으신가요?"
              rows={4}
            />
          </div>
          <div className={styles.inputGroup}>
            <label>해결 방안</label>
            <Textarea
              value={ideaForm.solution}
              onChange={(e) => setIdeaForm(prev => ({ ...prev, solution: e.target.value }))}
              placeholder="문제를 어떻게 해결하실 건가요?"
              rows={4}
            />
          </div>
          <div className={styles.inputGroup}>
            <label>기대 효과</label>
            <Textarea
              value={ideaForm.impact}
              onChange={(e) => setIdeaForm(prev => ({ ...prev, impact: e.target.value }))}
              placeholder="이 아이디어가 가져올 변화는 무엇인가요?"
              rows={4}
            />
          </div>
        </div>
        <div className={styles.modalActions}>
          <Button onClick={onSubmit} variant="primary" size="large" width="full-width">
            등록
          </Button>
        </div>
      </div>
    </>
  );
};

function Hackathon() {
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [isIdeaModalOpen, setIsIdeaModalOpen] = useState(false);
  const [ideaForm, setIdeaForm] = useState({
    title: '',
    problem: '',
    solution: '',
    impact: ''
  });
  const [sortType, setSortType] = useState('investment');
  const [showMyIdeasOnly, setShowMyIdeasOnly] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        fetchUserProfile(session.user.id);
      }
      fetchIdeas();
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    fetchIdeas();
  }, [sortType, showMyIdeasOnly]);

  const fetchUserProfile = async (userId) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) return;
    setUserProfile(data);
  };

  const fetchIdeas = async () => {
    try {
      let query = supabase
        .from('ideas')
        .select('*')
        .neq('status', 'cancel');
      
      if (showMyIdeasOnly && session?.user) {
        query = query.eq('user_id', session.user.id);
      }
      
      if (sortType === 'investment') {
        query = query.order('total_investment', { ascending: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data: ideasData, error: ideasError } = await query;
      if (ideasError) throw ideasError;
      setIdeas(ideasData);
    } catch (error) {
      console.error('Error fetching ideas:', error);
    }
  };

  const handleIdeaSubmit = async () => {
    if (!session?.user) return;

    try {
      const { error } = await supabase
        .from('ideas')
        .insert({
          user_id: session.user.id,
          title: ideaForm.title,
          problem: ideaForm.problem,
          solution: ideaForm.solution,
          impact: ideaForm.impact,
          total_investment: 0
        });

      if (error) {
        console.error('Error creating idea:', error);
        alert('아이디어 등록 중 오류가 발생했습니다.');
        return;
      }

      setIsIdeaModalOpen(false);
      setIdeaForm({
        title: '',
        problem: '',
        solution: '',
        impact: ''
      });
      fetchIdeas();
    } catch (error) {
      console.error('Error:', error);
      alert('아이디어 등록 중 오류가 발생했습니다.');
    }
  };

  const handleLoginClick = () => {
    navigate('/admin/login', { 
      state: { redirectTo: '/project/plabthon' }
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'pending') return null;

    const statusMap = {
      team_building: { label: '멤버 모집중', className: styles.statusTeamBuilding },
      team_confirmed: { label: '멤버 모집 완료', className: styles.statusTeamConfirmed },
      in_progress: { label: '달려!🔥', className: styles.statusInProgress }
    };

    const statusInfo = statusMap[status];
    if (!statusInfo) return null;

    return (
      <span className={`${styles.statusBadge} ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  return (
    <>
      <Helmet>
        <title>PLABTHON - 플랩 해커톤</title>
      </Helmet>

      <div className={styles.container}>
        <TitleSection />

        <main className={styles.main}>
          <div className={styles.controlBar}>
            <div className={styles.filterGroup}>
              <Checkbox
                checked={showMyIdeasOnly}
                onChange={(e) => setShowMyIdeasOnly(e.target.checked)}
                label="내 아이디어"
              />
            </div>
            <div className={styles.sortOptions}>
              <span
                className={`${styles.sortOption} ${sortType === 'investment' ? styles.active : ''}`}
                onClick={() => setSortType('investment')}
              >
                투자금액순
              </span>
              <span className={styles.sortDivider}>•</span>
              <span
                className={`${styles.sortOption} ${sortType === 'latest' ? styles.active : ''}`}
                onClick={() => setSortType('latest')}
              >
                최신순
              </span>
            </div>
          </div>

          <div className={styles.ideaList}>
            {ideas.map((idea) => (
              <Link
                key={idea.id}
                to={`/project/plabthon/${idea.id}`}
                className={styles.ideaCard}
              >
                <div className={styles.ideaHeader}>
                  {getStatusBadge(idea.status)}
                  <h3 className={styles.ideaTitle}>
                    {idea.title}
                    {session?.user?.id === idea.user_id && (
                      <span className={styles.myBadge}>MY</span>
                    )}
                  </h3>
                </div>
                <p className={styles.ideaValue}>
                  {idea.total_investment?.toLocaleString() || 0}원
                </p>
              </Link>
            ))}
          </div>
        </main>

        {session ? (
          <Button
            onClick={() => setIsIdeaModalOpen(true)}
            className={styles.floatingButton}
          >
            아이디어 등록
          </Button>
        ) : (
          <Button
            onClick={handleLoginClick}
            className={styles.floatingButton}
          >
            로그인하고 아이디어 등록하기
          </Button>
        )}

        <IdeaModal
          isOpen={isIdeaModalOpen}
          onClose={() => setIsIdeaModalOpen(false)}
          onSubmit={handleIdeaSubmit}
          ideaForm={ideaForm}
          setIdeaForm={setIdeaForm}
        />

        <TabMenu />
      </div>
    </>
  );
}

export default Hackathon; 